import React from 'react';
import styled, { css } from 'styled-components';
import scrollTop from '@hooks/resetScrollTop';
import { MENU_ITEM_TYPE } from '@typings/menu/menuItemType';
import Tooltip from '@components/dataDisplay/Tooltip';
import { IBoardSet } from '@typings/menu/boardSetType';
import { TOOLTIP_DIR_TYPE_VALUE } from '@typings/common/tooltipDirType';
import { MENU_TEXT_TYPE_VALUE } from '@typings/menu/menuTextType';
import { NavLink, useNavigate } from 'react-router-dom';
import { BOARD_TYPE } from '@typings/boardType';
import { cookieStore } from '@store/cookie/cookieStore';
import { useReadPermission } from '@hooks/usePermission';
import { LAYOUT_TYPE, LAYOUT_TYPE_VALUE } from '@typings/setting/layoutType';
import { ACCESS_TYPE } from '@typings/common/accessType';
import { imageAlignmentStyles, NavIcon } from './commonStyle';
import { isMasterState } from '@atom/setting/user';
import { useRecoilValue } from 'recoil';
import { homeSettingState } from '@atom/setting/homeSettingState';

const layoutTypeMap = {
  [LAYOUT_TYPE.GAMEUI]: css`
    width: fit-content;
  `,
  [LAYOUT_TYPE.TOP]: css`
    width: fit-content;
  `,
};

const StyledNavLink = styled(NavLink)<{
  $layoutType: LAYOUT_TYPE_VALUE; // 🎯 $ prefix로 DOM 전달 방지
}>`
  width: 100%;
  display: inline-flex;
  align-items: center;
  line-height: 0;
  vertical-align: top;

  ${({ $layoutType }) => layoutTypeMap[$layoutType]}
  &.active button {
    text-decoration: underline;
    text-underline-offset: 8px;
    filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.9));
  }
`;

const NavItemBtn = styled.button`
  padding: 8px 12px;
  min-height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex-shrink: 0;
  border: none;
  background: none;
  border-radius: var(--buttonBorderRadius);
  color: var(--menuTextColor);
  font-family: var(--menuFontFamily);
  font-size: 14px;
  transition: all 0.2s;
  z-index: 1;
  line-height: 1;

  &:hover {
    color: var(--primaryColor);
    cursor: var(--hoverCursorURL), pointer;
  }
`;

type Props = {
  list: IBoardSet;
  tooltipDir?: TOOLTIP_DIR_TYPE_VALUE;
  layoutType: LAYOUT_TYPE_VALUE;
  menuTextType: MENU_TEXT_TYPE_VALUE;
  menuItemType: any;
  isActive?: boolean;
  noChangeMobile?: boolean;
};

function NavItem({
  list,
  tooltipDir,
  layoutType,
  menuTextType,
  menuItemType,
  isActive,
  noChangeMobile,
}: Props) {
  const { path, title, imgURL, openNewTab, type } = list;
  const hoverImgURL = (list as any)?.hoverImgURL || '';
  const mobileImgURL = (list as any)?.mobileImgURL || '';
  const isMaster = useRecoilValue(isMasterState);
  const homeSet = useRecoilValue(homeSettingState) as any;

  const navigate = useNavigate();

  const readPermission = useReadPermission({
    accessType: list.accessType,
    authorUid: '',
  });

  const handleNavClick = () => {
    scrollTop();
    cookieStore.setOffset('0');
  };

  const useImgShadow = homeSet?.menu?.useImgShadow ?? false;
  const imgShadowColor = homeSet?.menu?.imgShadowColor ?? 'rgba(0,0,0,0.35)';

  const isMobile =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(max-width: 767px)').matches;
  const currentImg = noChangeMobile
    ? imgURL
    : (isMobile && mobileImgURL ? mobileImgURL : imgURL) || imgURL;
  const hasHover = !!hoverImgURL;

  const effectiveMenuItemType =
    menuItemType === MENU_ITEM_TYPE.IMAGE && !currentImg
      ? MENU_ITEM_TYPE.TEXT
      : menuItemType;

  const drawMap = {
    [MENU_ITEM_TYPE.IMAGE]: (
      <Tooltip message={title} direction={tooltipDir || 'right'} isPortal hidden={isMobile}>
        <NavIcon
          style={imageAlignmentStyles[menuTextType]}
          $useShadow={useImgShadow}
          $shadowColor={imgShadowColor}
          $hasHover={!isMobile && hasHover}
          $disableMaxHeight={layoutType === LAYOUT_TYPE.TOP}
        >
          <img
            className="base-img"
            src={currentImg}
            alt={title}
            loading="lazy"
          />
          {hoverImgURL && !isMobile && (
            <img
              className="hover-img"
              src={hoverImgURL}
              alt={`${title}-hover`}
              loading="lazy"
            />
          )}
        </NavIcon>
      </Tooltip>
    ),
    [MENU_ITEM_TYPE.TEXT]: <NavItemBtn>{title}</NavItemBtn>,
  };

  if (!readPermission) return null;
  // 🎯 비공개 메뉴는 아예 보이지 않게 처리
  if (list.accessType === ACCESS_TYPE.SECRET && !isMaster) {
    return null;
  }

  if (type === BOARD_TYPE.LINK) {
    // 커스텀메뉴형 링크 처리
    const isExternalURL = (p: string) =>
      p.startsWith('http://') || p.startsWith('https://');

    const getInternalPath = (p: string) => {
      // '/'로 시작하면 그대로, 아니면 앞에 '/' 붙이기
      return p.startsWith('/') ? p : `/${p}`;
    };

    const handleLinkClick = () => {
      if (!path) return;

      if (isExternalURL(path)) {
        // 외부 URL: 새 탭 또는 전체 이동
        if (openNewTab) {
          window.open(path, '_blank', 'noreferrer');
        } else {
          window.location.href = path;
        }
      } else {
        // 내부 경로: SPA 라우터로 이동 (새로고침 없음)
        const internalPath = getInternalPath(path);
        if (openNewTab) {
          window.open(`${window.location.origin}${internalPath}`, '_blank', 'noreferrer');
        } else {
          navigate(internalPath);
          handleNavClick();
        }
      }
    };

    return (
      <div data-sound-type="menu" onClick={handleLinkClick}>
        {drawMap[effectiveMenuItemType]}
      </div>
    );
  }

  return (
    <StyledNavLink
      $layoutType={layoutType || LAYOUT_TYPE.TOP}
      to={`/${path}`}
      onClick={handleNavClick}
      data-sound-type="menu"
      className={({ isActive }) => (isActive ? 'active' : '')}
      style={{ textAlign: menuTextType }}
    >
      {drawMap[effectiveMenuItemType]}
    </StyledNavLink>
  );
}

export default NavItem;
